import React, { useEffect, useRef } from 'react';
import { FileTextIcon, Award, TrendingUp, Users, Briefcase } from 'lucide-react';
import { educationData, skillsData } from '../utils/constants';
import { motion } from 'framer-motion';
const About = ({
  language = 'en'
}) => {
  const translations = {
    en: {
      about: 'About',
      me: 'Me',
      tagline: 'Finance Student • Asset Management • Investment Banking',
      intro: "I'm a finance student at Northeastern University combining Finance with Data Science. With experience across asset management, investment banking, trading, and real estate PE, I bring a comprehensive understanding of financial markets and investment strategies.",
      downloadResume: 'Download Resume',
      featuredStockPitch: 'Featured Stock Pitch',
      stats: {
        experience: 'Years Experience',
        projects: 'Projects Completed',
        impact: 'Lives Impacted',
        gpa: 'GPA'
      },
      cards: {
        education: 'Education',
        skills: 'Top Skills',
        interests: 'Interests'
      }
    },
    vi: {
      about: 'Giới',
      me: 'Thiệu',
      tagline: 'Sinh viên Tài chính • Quản lý Tài sản • Ngân hàng Đầu tư',
      intro: 'Tôi là sinh viên tài chính tại Đại học Northeastern kết hợp Tài chính với Khoa học Dữ liệu. Với kinh nghiệm về quản lý tài sản, ngân hàng đầu tư, giao dịch và quỹ đầu tư bất động sản, tôi có hiểu biết toàn diện về thị trường tài chính và chiến lược đầu tư.',
      downloadResume: 'Tải CV',
      featuredStockPitch: 'Phân Tích Cổ Phiếu',
      stats: {
        experience: 'Năm Kinh Nghiệm',
        projects: 'Dự Án Hoàn Thành',
        impact: 'Người Được Hỗ Trợ',
        gpa: 'GPA'
      },
      cards: {
        education: 'Học Vấn',
        skills: 'Kỹ Năng Chính',
        interests: 'Sở Thích'
      }
    }
  };
  const t = translations[language];
  const stats = [{
    label: t.stats.experience,
    value: '2+',
    icon: Briefcase,
    color: 'text-blue-500'
  }, {
    label: t.stats.projects,
    value: '10+',
    icon: TrendingUp,
    color: 'text-green-500'
  }, {
    label: t.stats.impact,
    value: '3.5K+',
    icon: Users,
    color: 'text-purple-500'
  }, {
    label: t.stats.gpa,
    value: '3.8',
    icon: Award,
    color: 'text-yellow-500'
  }];
  return <section id="about" className="w-full py-24 bg-background relative overflow-hidden" data-id="element-17">
      <div className="container mx-auto px-4" data-id="element-18">
        {/* Header Section */}
        <div className="text-center mb-16" data-id="element-19">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-display font-bold mb-4" data-id="element-20">
            {t.about} <span className="gradient-text" data-id="element-21">{t.me}</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-muted max-w-2xl mx-auto" data-id="element-22">
            {t.tagline}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-23">
          {/* Left Column: Profile & Intro */}
          <div className="lg:col-span-5 space-y-8" data-id="element-24">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative group" data-id="element-25">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" data-id="element-26" />
              <div className="relative aspect-square rounded-3xl overflow-hidden border-2 border-white/10 shadow-2xl" data-id="element-27">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oz2JDEbs664H64BHe3SMGd/Trung_Nguyen_Design.png" alt="Trung Nguyen" className="w-full h-full object-cover" style={{
                objectPosition: 'center top'
              }} data-id="element-28" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5" data-id="element-29">
              <p className="text-lg leading-relaxed text-muted mb-6" data-id="element-30">
                {t.intro}
              </p>
              <a href="https://drive.google.com/file/d/1xng45TtT45hhH3f0A9TH7KE0bOJ4IsvJ/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-31">
                <FileTextIcon size={20} data-id="element-32" />
                {t.downloadResume}
              </a>
            </motion.div>
          </div>

          {/* Right Column: Stats & Details */}
          <div className="lg:col-span-7 space-y-8" data-id="element-33">
            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-34">
              {stats.map((stat, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="bg-secondary/30 rounded-2xl p-4 text-center border border-white/5 hover:bg-secondary/50 transition-colors" data-id="element-35">
                  <stat.icon className={`w-6 h-6 mx-auto mb-2 ${stat.color}`} data-id="element-36" />
                  <div className="text-2xl font-bold mb-1" data-id="element-37">{stat.value}</div>
                  <div className="text-xs text-muted uppercase tracking-wider" data-id="element-38">
                    {stat.label}
                  </div>
                </motion.div>)}
            </div>

            {/* Info Cards */}
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-39">
              {/* Education Card */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-secondary/30 rounded-2xl p-6 border border-white/5 hover:border-blue-500/30 transition-colors" data-id="element-40">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-41">
                  <span className="w-1 h-6 bg-blue-500 rounded-full" data-id="element-42" />
                  {t.cards.education}
                </h3>
                <div className="space-y-2" data-id="element-43">
                  <p className="font-medium text-lg" data-id="element-44">{educationData.school}</p>
                  <p className="text-muted" data-id="element-45">{educationData.degree}</p>
                  <p className="text-sm text-blue-500" data-id="element-46">{educationData.minor}</p>
                </div>
              </motion.div>

              {/* Skills Card */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-secondary/30 rounded-2xl p-6 border border-white/5 hover:border-purple-500/30 transition-colors" data-id="element-47">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-48">
                  <span className="w-1 h-6 bg-purple-500 rounded-full" data-id="element-49" />
                  {t.cards.skills}
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-50">
                  {skillsData.technical.slice(0, 6).map((skill, i) => <span key={i} className="px-3 py-1 bg-white/5 rounded-full text-sm border border-white/10" data-id="element-51">
                      {skill}
                    </span>)}
                </div>
              </motion.div>
            </div>

            {/* Stock Pitch Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-secondary/30 rounded-2xl p-6 border border-white/5" data-id="element-52">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-53">
                <span className="w-1 h-6 bg-green-500 rounded-full" data-id="element-54" />
                {t.featuredStockPitch}
              </h3>
              <div className="aspect-video rounded-xl overflow-hidden bg-black/20 shadow-inner" data-id="element-55">
                <iframe src="https://drive.google.com/file/d/1q19z9lHsqAQtf-WYXIylRENHNE0mcs_L/preview" className="w-full h-full" allow="autoplay" title="Stock Pitch" data-id="element-56" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
};
export default About;